import type { RootState } from '~/store/store.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import { jwtDecode } from 'jwt-decode';
import type { TManagerDecodedToken } from './types.ts';

type TUserEmailTokenPayload = Partial<TManagerDecodedToken> & {
  preferred_username?: string;
  user_name?: string;
  username?: string;
};

const getStringValue = (value: unknown): string => String(value ?? '').trim();

const getUserEmailFromToken = (token?: string | null): string => {
  if (!token) {
    return '';
  }

  try {
    const decodedToken = jwtDecode<TUserEmailTokenPayload>(token);

    return (
      getStringValue(decodedToken.email) ||
      getStringValue(decodedToken.preferred_username) ||
      getStringValue(decodedToken.username) ||
      getStringValue(decodedToken.user_name) ||
      getStringValue(decodedToken.sub)
    );
  } catch {
    return '';
  }
};

export const selectSupervisorCovenantsRights = (s: RootState) =>
  s.users.supervisorInfo.data ?? s.users.user.rights?.covenants ?? null;
export const selectShouldFetchSupervisorInfo = (s: RootState) =>
  s.users.supervisorInfo.status === FetchStatus.Idle && !selectSupervisorCovenantsRights(s);
export const selectIsSupervisorInfoLoading = (s: RootState) =>
  s.users.supervisorInfo.status === FetchStatus.Loading;
export const selectHasSupervisorApplicationAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasApplicationAccess;
export const selectHasSupervisorAdministrationAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasAdministrationAccess;
export const selectHasSupervisorCallCenterOperatorAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasCallCenterOperatorAccess;
export const selectHasSupervisorBranchManagerAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasBranchManagerAccess;
export const selectHasSupervisorClientViewAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasClientViewAccess;
export const selectHasSupervisorEmailBlackListAccess = (s: RootState) =>
  !!selectSupervisorCovenantsRights(s)?.hasEmailBlackListAccess;
export const selectUserLoading = (state: RootState) => state.users?.loading;
export const selectUserError = (state: RootState) => state.users.error;
export const selectToken = (state: RootState) => state.users.token;
export const selectCurrentUserEmail = (state: RootState) =>
  getStringValue(state.users.user.email) || getUserEmailFromToken(state.users.token);
export const selectTokenData = (state: RootState) => state.users.user.tokenData;
export const selectTokenDataRoles = (state: RootState) => state.users.user.roles || [];
